"use client"

import * as React from "react"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import TextWithBr from "@/components/ui/text-with-br"
import { useState, useEffect } from "react"

type Feature = {
  title: string
  description: string
  image: string
}

const FEATURES: Feature[] = [
  {
    title: "Mobile App",
    description:
      "Empower members with iOS and Android apps for provider search, digital ID cards, reimbursements, feedback, and real-time claim updates.",
    image: "/images/dami/mobile-app.png?height=1000&width=565",
  },
  {
    title: "Claims Automation.",
    description:
      "Process claims faster and more accurately with AI-powered validation, fraud detection, and policy-based adjudication.",
    image: "/images/claims-automation.jpg?height=1000&width=565",
  },
  {
    title: "Members Management.",
    description:
      "Onboard, enroll, and manage members seamlessly with full visibility into benefits, dependents, renewals, and utilization patterns.",
    image: "/images/member-management.jpg?height=1000&width=565",
  },
  {
    title: "Pre-Authorizations",
    description: "Simplify approvals through automated rules, digital documentation, and real-time authorization tracking.",
    image: "/images/pre-authorizations.jpg?height=1000&width=565",
  },
  {
    title: "Provider Management.",
    description:
      "Digitally manage contracts, monitor performance, and maintain a dynamic provider directory with live tariffs and payments.",
    image: "/images/provider-management.jpg?height=1000&width=565",
  },
  {
    title: "Pharmacy & Chronic Care",
    description:
      "Automate prescription refills, approvals, and pharmacy claims while tracking adherence and optimizing chronic care management.",
    image: "/images/pharmacy-benefits.jpg?height=1000&width=565",
  },
  {
    title: "Underwriting & Plan Configuration.",
    description:
      "Configure complex products in minutes — define benefits, limits, exclusions, and pricing rules across all plan types.",
    image: "/images/underwriting-plan.jpg?height=1000&width=565",
  },
  {
    title: "CRM & Communication.",
    description:
      "Centralize all member interactions and send automated updates through email, SMS, or WhatsApp for improved engagement.",
    image: "/images/crm-communication.jpg?height=1000&width=565",
  },
  {
    title: "Reporting & Analytics.",
    description:
      "Turn data into decisions with real-time dashboards, utilization trends, and deep insights into claims and provider performance.",
    image: "/images/reporting-analytics.jpg?height=1000&width=565",
  },
]

export function ProductFeaturesSection() {
  const [activeIndex, setActiveIndex] = React.useState(0)
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null)
  const listRef = React.useRef<HTMLDivElement>(null)
  const stepRefs = React.useRef<(HTMLDivElement | null)[]>([])
  const [progress, setProgress] = React.useState(0)
  const parallaxRef = React.useRef<HTMLDivElement>(null)
  
  // Mobile slider state
  const [mobileActiveIndex, setMobileActiveIndex] = useState(0)
  const [isAutoSliding, setIsAutoSliding] = useState(true)
  React.useEffect(() => {
    setActiveIndex(0)
    stepRefs.current = []
  }, [])

  // Auto-sliding effect for mobile
  useEffect(() => {
    if (!isAutoSliding) return
    
    const interval = setInterval(() => {
      setMobileActiveIndex((prev) => (prev + 1) % FEATURES.length)
    }, 4000) // Change slide every 4 seconds
    
    return () => clearInterval(interval)
  }, [isAutoSliding])

  React.useEffect(() => {
    const rootEl = listRef.current
    const els = stepRefs.current.filter(Boolean) as HTMLDivElement[]
    if (els.length === 0 || !rootEl) return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio)
          const idx = Number((visible[0].target as HTMLElement).dataset.index || 0)
          setActiveIndex(idx)
        }
      },
      { root: rootEl, threshold: [0.5] },
    )
    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  React.useEffect(() => {
    const el = listRef.current
    if (!el) return
    const update = () => {
      const max = el.scrollHeight - el.clientHeight
      const p = max > 0 ? el.scrollTop / max : 0
      setProgress(Math.min(Math.max(p, 0), 1))
    }
    update()
    el.addEventListener("scroll", update, { passive: true })
    window.addEventListener("resize", update)
    return () => {
      el.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
    }
  }, [])

  

  const handleParallaxMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = parallaxRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width - 0.5
    const y = (e.clientY - rect.top) / rect.height - 0.5
    el.style.transform = `perspective(800px) rotateY(${x * 6}deg) rotateX(${-y * 6}deg) scale(1.02)`
  }
  const handleParallaxLeave = () => {
    const el = parallaxRef.current
    if (!el) return
    el.style.transform = "perspective(800px) rotateY(0deg) rotateX(0deg) scale(1)"
  }

  return (
    <section id="product" className="py-20 bg-gradient-to-br from-white via-[#1785CA]/5 to-white overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <h2 className="text-3xl lg:text-5xl font-light text-gray-900">Purpose-Built for Smarter Health Insurance Operations</h2>
          <p className="mt-4 text-[0.9rem] sm:text-lg text-gray-600">
            Every OctoHealth module is intelligently designed to streamline even the most complex workflows
          </p>
        </div>

        {/* Removed hero-like/accordion block per redesign request */}
        <div className="hidden md:block">
          <div className="grid md:grid-cols-12 gap-8">
            {/* Left stage: sticky full-cover image */}
            <div className="md:col-span-6">
              <Card className="h-[560px] bg-white/60 backdrop-blur-md border border-white/30 shadow-xl rounded-3xl overflow-hidden sticky top-24">
                <CardContent className="p-0 h-full stage-card">
                  <div
                    className="relative w-full h-full overflow-hidden bg-white/50 backdrop-blur-sm"
                    onMouseMove={handleParallaxMove}
                    onMouseLeave={handleParallaxLeave}
                  >
                    <div ref={parallaxRef} className="w-full h-full">
                      <img
                        src={(FEATURES[hoveredIndex ?? activeIndex]?.image) || "/placeholder.svg"}
                        alt={`${FEATURES[hoveredIndex ?? activeIndex]?.title || "Feature"} screenshot`}
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* Right steps: scroll-driven list with progress line */}
            <div className="md:col-span-6 relative h-[560px] overflow-y-auto no-scrollbar scroll-fade" ref={listRef}>
              <div className="absolute left-1 top-0 bottom-0 w-px bg-gray-200" />
              <div
                className="absolute left-1 top-0 w-px bg-[#1a85ca] transition-[height] duration-300"
                style={{ height: `${Math.round(progress * 100)}%` }}
                aria-hidden="true"
              />
              <div className="space-y-6 pl-6">
                {FEATURES.map((feature, i) => (
                  <div
                    key={`step-${feature.title}-${i}`}
                    ref={(el) => {
                      stepRefs.current[i] = el
                    }}
                    data-index={i}
                    className={cn("group cursor-pointer")}
                    onClick={() => setActiveIndex(i)}
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <Card
                      className={cn(
                        "bg-white/70 backdrop-blur-md border border-white/30 shadow-lg rounded-2xl overflow-hidden transition-all duration-300",
                        i === activeIndex ? "ring-1 ring-[#1a85ca]/40 shadow-xl" : "hover:shadow-xl hover:translate-x-0.5",
                      )}
                    >
                      <CardContent className="p-5 flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-white shadow-md ring-1 ring-gray-200 flex items-center justify-center text-gray-700 font-semibold">
                          {i + 1}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{feature.title}</div>
                          <TextWithBr as="div" className="text-sm text-gray-600 mt-1" text={feature.description} />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="md:hidden">
          {/* Mobile Sliding Carousel */}
          <div className="relative overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${mobileActiveIndex * 100}%)` }}
            >
              {FEATURES.map((feature, index) => (
                <div key={`mobile-${feature.title}-${index}`} className="w-full flex-shrink-0 px-4">
                  <MobileFeatureCard feature={feature} />
                </div>
              ))}
            </div>
            
            {/* Dots indicator */}
            <div className="flex justify-center mt-4 space-x-2">
              {FEATURES.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setMobileActiveIndex(index)
                    setIsAutoSliding(false)
                    // Resume auto-sliding after 10 seconds
                    setTimeout(() => setIsAutoSliding(true), 10000)
                  }}
                  className={cn(
                    "w-2 h-2 rounded-full transition-colors duration-200",
                    index === mobileActiveIndex 
                      ? "bg-[#1785CA]" 
                      : "bg-gray-300 hover:bg-gray-400"
                  )}
                />
              ))}
            </div>
            
            {/* Touch/Swipe area overlay */}
            <div 
              className="absolute inset-0 touch-pan-y"
              onTouchStart={(e) => {
                setIsAutoSliding(false)
                const startX = e.touches[0].clientX
                
                const handleTouchEnd = (endEvent: TouchEvent) => {
                  const endX = endEvent.changedTouches[0].clientX
                  const diff = startX - endX
                  
                  if (Math.abs(diff) > 50) { // Minimum swipe distance
                    if (diff > 0 && mobileActiveIndex < FEATURES.length - 1) {
                      setMobileActiveIndex(prev => prev + 1)
                    } else if (diff < 0 && mobileActiveIndex > 0) {
                      setMobileActiveIndex(prev => prev - 1)
                    }
                  }
                  
                  // Resume auto-sliding after 10 seconds
                  setTimeout(() => setIsAutoSliding(true), 10000)
                  document.removeEventListener('touchend', handleTouchEnd)
                }
                
                document.addEventListener('touchend', handleTouchEnd)
              }}
            />
          </div>
        </div>
      </div>

      <style jsx>{`
        .stage-card { transition: opacity 300ms ease, transform 300ms ease; }
        /* Hide scrollbars cross-browser */
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .no-scrollbar::-webkit-scrollbar { width: 0; height: 0; }
        /* Subtle fade at top/bottom to de-emphasize container edges */
        .scroll-fade { 
          -webkit-mask-image: linear-gradient(to bottom, transparent, black 24px, black calc(100% - 24px), transparent);
                  mask-image: linear-gradient(to bottom, transparent, black 24px, black calc(100% - 24px), transparent);
        }
      `}</style>
    </section>
  )
}

function DesktopFeatureCard({ feature }: { feature: Feature }) {
  return (
    <Card className="w-[102vw] max-w-[1056px] h-[460px] bg-white/60 backdrop-blur-md border border-white/30 shadow-xl hover:shadow-3xl rounded-3xl overflow-hidden flex-shrink-0 transition-transform duration-300 hover:scale-[1.01]">
      <CardContent className="p-0 h-full flex">
        <div className="flex-1 p-14 flex flex-col justify-center">
          <h3 className="text-4xl font-bold text-gray-900 mb-7 leading-tight">{feature.title}</h3>
          <TextWithBr as="div" className="text-xl text-gray-600 leading-relaxed" text={feature.description} />
        </div>
        <div className="w-96 relative bg-white/50 backdrop-blur-sm border-l border-white/30">
          <img
            src={feature.image || "/placeholder.svg"}
            alt={`${feature.title} illustration`}
            className="w-full h-full object-cover"
            crossOrigin="anonymous"
          />
        </div>
      </CardContent>
    </Card>
  )
}

function MobileFeatureCard({ feature }: { feature: Feature }) {
  return (
    <Card className="w-[90%] mx-auto min-w-[288px] bg-white/70 backdrop-blur-md border border-white/30 shadow-lg hover:shadow-2xl rounded-2xl overflow-hidden transition-transform duration-300 hover:scale-[1.01]">
      <CardContent className="p-0">
        <div className="w-full h-48 relative bg-white/50 backdrop-blur-sm border-b border-white/30">
          <img
            src={feature.image || "/placeholder.svg"}
            alt={`${feature.title} illustration`}
            className="w-full h-full object-cover"
            crossOrigin="anonymous"
          />
        </div>

        <div className="p-6 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-4 leading-tight">{feature.title}</h3>
          <TextWithBr as="div" className="text-base text-gray-600 leading-relaxed" text={feature.description} />
        </div>
      </CardContent>
    </Card>
  )
}
